import React, { useState, useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
  debounce,
} from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
  getGridStringOperators,
} from "@mui/x-data-grid-pro";
import { axiosWithAuth } from "../plugins/axios";
import { useNavigate } from "react-router-dom";
import { useRole } from "../lib/contexts/RoleContext";
import { getAPIUrlByRole } from "../lib/utils/config";
import DownloadIcon from "@mui/icons-material/Download";
import saveAs from "file-saver";
import Papa from "papaparse";
import to from "await-to-js";

interface PendingEvaluation {
  _id: string; // evaluationId
  asset: {
    _id: string; // assetId
    name: string; // assetName
    code: string;
    version: number;
  };
  program: {
    _id: string;
    code: string;
    name: string; // programName
  };
  workshop: {
    _id: string;
    code: string; // cohortId
  };
  version: number; // assetVersion
  createdBy: {
    _id: string; // studentId
    email: string;
    firstName: string;
    lastName: string; // studentName (firstName + lastName)
  };
  assignedAt: string; // assigned to AG on
  cohort: {
    _id: string;
    asset: string;
    version: number;
    status: string; // status on AG
  };
  autoGrade: {
    submissionId: string;
    status: string;
  } | null;
}

const EvaluationNotStartedReport = () => {
  const [pendingEvaluations, setPendingEvaluations] = useState<PendingEvaluation[]>([]);
  const [allPendingEvaluations, setAllPendingEvaluations] = useState<PendingEvaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [assetType, setAssetType] = useState<"assignment" | "project">("assignment");
  const [assignmentData, setAssignmentData] = useState<PendingEvaluation[]>([]);
  const [projectData, setProjectData] = useState<PendingEvaluation[]>([]);
  const [assignmentAllData, setAssignmentAllData] = useState<PendingEvaluation[]>([]);
  const [projectAllData, setProjectAllData] = useState<PendingEvaluation[]>([]);
  const [assignmentTotalCount, setAssignmentTotalCount] = useState(0);
  const [projectTotalCount, setProjectTotalCount] = useState(0);

  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const navigate = useNavigate();
  const role = useRole();
  const apiUrl = getAPIUrlByRole(role);

  useEffect(() => {
    const fetchAllData = async () => {
      setError(null);
      setLoading(true);
      try {
        // Fetch both assignment and project data in parallel
        const [assignmentResponse, projectResponse] = await Promise.all([
          axiosWithAuth(
            `${apiUrl}autoGrade/pendingsubmissions/assignment`,
            {
              params: {
                page: page + 1,
                limit: rowsPerPage,
              },
            },
            "get"
          ),
          axiosWithAuth(
            `${apiUrl}autoGrade/pendingsubmissions/project`,
            {
              params: {
                page: page + 1,
                limit: rowsPerPage,
              },
            },
            "get"
          )
        ]);

        setAssignmentData(assignmentResponse.data?.data || []);
        setProjectData(projectResponse.data?.data || []);
        setAssignmentTotalCount(assignmentResponse.data?.totalCount || 0);
        setProjectTotalCount(projectResponse.data?.totalCount || 0);
        
        // Set initial data based on default assetType
        setPendingEvaluations(assignmentResponse.data?.data || []);
        setTotalCount(assignmentResponse.data?.totalCount || 0);

      } catch (err) {
        setError("Failed to load pending evaluations");
        console.error("Error fetching pending evaluations:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [apiUrl, page, rowsPerPage]);

  useEffect(() => {
    const fetchAllDataForExport = async () => {
      try {
        // Fetch all data for both assignment and project for export
        const [assignmentResponse, projectResponse] = await Promise.all([
          axiosWithAuth(
            `${apiUrl}autoGrade/pendingAllsubmissions/assignment`,
            {},
            "get"
          ),
          axiosWithAuth(
            `${apiUrl}autoGrade/pendingAllsubmissions/project`,
            {},
            "get"
          )
        ]);

        setAssignmentAllData(assignmentResponse.data?.data || []);
        setProjectAllData(projectResponse.data?.data || []);
        setAllPendingEvaluations(assignmentResponse.data?.data || []);

      } catch (err) {
        console.error("Failed to load all pending evaluations for export", err);
      }
    };

    fetchAllDataForExport();
  }, [apiUrl]);

  // Update displayed data when assetType changes
  useEffect(() => {
    if (assetType === "assignment") {
      setPendingEvaluations(assignmentData);
      setAllPendingEvaluations(assignmentAllData);
      setTotalCount(assignmentTotalCount);
    } else {
      setPendingEvaluations(projectData);
      setAllPendingEvaluations(projectAllData);
      setTotalCount(projectTotalCount);
    }
  }, [assetType, assignmentData, projectData, assignmentAllData, projectAllData, assignmentTotalCount, projectTotalCount]);

  const debounceFetchPendingEvaluations = useMemo(
    () =>
      debounce((filter) => {
        setFilterModel(filter);
      }, 1000),
    [filterModel]
  );

  const handleAssetTypeChange = (newAssetType: "assignment" | "project") => {
    if (newAssetType !== assetType) {
      setAssetType(newAssetType);
      setPage(0); // Reset to first page when changing asset type
    }
  };

  const exportEvaluations = () => {
    if (!allPendingEvaluations?.length) {
      console.warn("No data to export");
      setToastMessage("No data available to export");
      setToastOpen(true);
      return;
    }

    const rows = allPendingEvaluations.map((row) => ({
      "Evaluation ID": row._id,
      "Asset ID": row.asset?._id || "-",
      "Asset Name": row.asset?.name || "-",
      "Asset Version": row.version || "-",
      "Program Name": row.program?.name || "-",
      "Cohort ID": row.workshop?.code || "-",
      "Assigned to AG on": row.assignedAt ? new Date(row.assignedAt).toLocaleString() : "-",
      "Student ID": row.createdBy?._id || "-",
      "Student Name": `${row.createdBy?.firstName || ""} ${row.createdBy?.lastName || ""}`.trim() || "-",
      "Status on AG": row.autoGrade?.status || "-",
      "CohortConfig Status": row.cohort?.status || "-",
    }));

    const csv = Papa.unparse(rows, {
      quotes: true,
      delimiter: ",",
      newline: "\n",
      escapeChar: '"',
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `pendingEvaluations_${assetType}.csv`);
  };

  const getCustomFilterOperators = () =>
    getGridStringOperators().filter(
      (operator) => operator.value === "contains" || operator.value === "equals"
    );

  const columns: GridColDef[] = [
    {
      field: "_id",
      headerName: "Evaluation ID",
      width: 200,
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "assetId",
      headerName: "Asset ID",
      width: 200,
      renderCell: (params: GridRenderCellParams) => params.row.asset?._id || "-",
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "assetName",
      headerName: "Asset Name",
      width: 300,
      renderCell: (params: GridRenderCellParams) => params.row.asset?.name || "-",
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "version",
      headerName: "Asset Version",
      width: 120,
      renderCell: (params: GridRenderCellParams) => params.row.version || "-",
      filterable: false,
    },
    {
      field: "programName",
      headerName: "Program Name",
      width: 300,
      renderCell: (params: GridRenderCellParams) => params.row.program?.name || "-",
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "cohortId",
      headerName: "Cohort ID",
      width: 150,
      renderCell: (params: GridRenderCellParams) => params.row.workshop?.code || "-",
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "assignedAt",
      headerName: "Assigned to AG on",
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        return params.value ? new Date(params.value).toLocaleString() : "-";
      },
      filterable: false,
    },
    {
      field: "studentId",
      headerName: "Student ID",
      width: 200,
      renderCell: (params: GridRenderCellParams) => params.row.createdBy?._id || "-",
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "studentName",
      headerName: "Student Name",
      width: 200,
      renderCell: (params: GridRenderCellParams) => 
        `${params.row.createdBy?.firstName || ""} ${params.row.createdBy?.lastName || ""}`.trim() || "-",
      filterOperators: getCustomFilterOperators(),
    },
    {
      field: "status",
      headerName: "Status on AG",
      width: 150,
      renderCell: (params: GridRenderCellParams) => params.row.autoGrade?.status || "-",
      filterOperators: getCustomFilterOperators(),
    },{
      field: "statusAG",
      headerName: "CohortConfig Status",
      width: 150,
      renderCell: (params: GridRenderCellParams) => params.row.cohort?.status || "-",
      filterOperators: getCustomFilterOperators(),
    }
  ];

  if (loading) {
    return (
      <Box mt={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom mb={4}>
        Evaluation Not Started Report
      </Typography>
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <ButtonGroup disableElevation sx={{ borderRadius: 0 }}>
          <Button
            variant="contained"
            sx={{
              width: "150px",
              backgroundColor: assetType === "assignment" ? "#1565c0" : "#e3f2fd",
              color: assetType === "assignment" ? "#ffffff" : "#000000",
              "&:hover": {
                backgroundColor: assetType === "assignment" ? "#0d47a1" : "#bbdefb",
              },
              borderRadius: 0,
            }}
            onClick={() => handleAssetTypeChange("assignment")}
          >
            Assignment
          </Button>
          <Button
            variant="contained"
            sx={{
              width: "150px",
              backgroundColor: assetType === "project" ? "#1565c0" : "#e3f2fd",
              color: assetType === "project" ? "#ffffff" : "#000000",
              "&:hover": {
                backgroundColor: assetType === "project" ? "#0d47a1" : "#bbdefb",
              },
              borderRadius: 0,
            }}
            onClick={() => handleAssetTypeChange("project")}
          >
            Project
          </Button>
        </ButtonGroup>
        
        <Button
          variant="outlined"
          onClick={exportEvaluations}
          disabled={totalCount <= 0}
          startIcon={<DownloadIcon />}
        >
          Export Dashboard
        </Button>
      </Box>

      <Box mt={4}>
        <DataGridPro
          rows={pendingEvaluations}
          columns={columns}
          getRowId={(row) => row._id}
          rowHeight={70}
          paginationMode="server"
          sortingMode="server"
          pagination
          paginationModel={{
            page: page,
            pageSize: rowsPerPage,
          }}
          rowCount={totalCount}
          onPaginationModelChange={(newPaginationModel) => {
            setPage(newPaginationModel.page);
            setRowsPerPage(newPaginationModel.pageSize);
          }}
          pageSizeOptions={[5, 10, 25, 50]}
          filterMode="server"
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          onFilterModelChange={(filter) =>
            debounceFetchPendingEvaluations(filter)
          }
        />
      </Box>

      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EvaluationNotStartedReport;